<script setup>
import { computed, ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Tick02Icon, TickDouble02Icon } from '@hugeicons/core-free-icons';
import { MESSAGE_STATUS } from './constants';

import Icon from 'next/icon/Icon.vue';
import HugeIcon from 'next/icon/HugeIcon.vue';

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  /** Multi-line delivery details; shown on hover when set */
  timelineTooltip: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const progresIconSequence = [
  'i-lucide-clock-1',
  'i-lucide-clock-2',
  'i-lucide-clock-3',
  'i-lucide-clock-4',
  'i-lucide-clock-5',
  'i-lucide-clock-6',
  'i-lucide-clock-7',
  'i-lucide-clock-8',
  'i-lucide-clock-9',
  'i-lucide-clock-10',
  'i-lucide-clock-11',
  'i-lucide-clock-12',
];

const progessIcon = ref(progresIconSequence[0]);

const rotateIcon = () => {
  const currentIndex = progresIconSequence.indexOf(progessIcon.value);
  const nextIndex = (currentIndex + 1) % progresIconSequence.length;
  progessIcon.value = progresIconSequence[nextIndex];
};

useIntervalFn(rotateIcon, 500, {
  immediate: props.status === MESSAGE_STATUS.PROGRESS,
  immediateCallback: false,
});

const hugeStatusIcon = computed(() => {
  if (props.status === MESSAGE_STATUS.SENT) return Tick02Icon;
  if (
    props.status === MESSAGE_STATUS.DELIVERED ||
    props.status === MESSAGE_STATUS.READ
  ) {
    return TickDouble02Icon;
  }
  return Tick02Icon;
});

const statusColor = computed(() => {
  const statusIconMap = {
    [MESSAGE_STATUS.SENT]: 'text-n-slate-10',
    [MESSAGE_STATUS.DELIVERED]: 'text-n-slate-10',
    [MESSAGE_STATUS.READ]: 'text-[#7EB6FF]',
  };

  return statusIconMap[props.status];
});

const tooltipText = computed(() => {
  const statusTextMap = {
    [MESSAGE_STATUS.SENT]: t('CHAT_LIST.SENT'),
    [MESSAGE_STATUS.DELIVERED]: t('CHAT_LIST.DELIVERED'),
    [MESSAGE_STATUS.READ]: t('CHAT_LIST.MESSAGE_READ'),
    [MESSAGE_STATUS.PROGRESS]: t('CHAT_LIST.SENDING'),
  };

  return statusTextMap[props.status];
});

const displayTooltip = computed(() => {
  if (props.timelineTooltip) return props.timelineTooltip;
  return tooltipText.value;
});
</script>

<template>
  <!-- v-tooltip must bind to a real DOM node; Icon's root is <component> so wrap span -->
  <span
    v-if="status === MESSAGE_STATUS.PROGRESS"
    v-tooltip.top-start="displayTooltip"
    class="inline-flex items-center justify-center"
  >
    <Icon :icon="progessIcon" class="text-n-slate-10" />
  </span>
  <span
    v-else
    v-tooltip.top-start="displayTooltip"
    class="inline-flex items-center justify-center"
    :class="statusColor"
  >
    <HugeIcon
      :icon="hugeStatusIcon"
      :size="21"
      class="pointer-events-none h-[21px] w-[17.92px] shrink-0"
    />
  </span>
</template>
